/**
 * FirstBadgeCelebration.jsx — v1.0.0 Gate 5.5 Item 3
 *
 * Listens for the `first-badge-earned` window event dispatched by the axios
 * interceptor in api.js and shows a center-screen celebratory modal using the
 * Unified Badge component at feature size (64px).
 *
 * Fires ONCE (localStorage gated in api.js).
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Badge } from "@/components/shared/Badge";
import { Sparkles, X } from "lucide-react";

export default function FirstBadgeCelebration() {
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    const handler = (e) => setBadge(e.detail || null);
    window.addEventListener("first-badge-earned", handler);
    return () => window.removeEventListener("first-badge-earned", handler);
  }, []);

  useEffect(() => {
    if (!badge) return;
    const timer = setTimeout(() => setBadge(null), 6000);
    return () => clearTimeout(timer);
  }, [badge]);

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="first-badge-celebration"
        >
          <div className="absolute inset-0 bg-black/60" onClick={() => setBadge(null)} />
          <motion.div
            initial={{ scale: 0.5, rotate: -8, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 16, stiffness: 260 }}
            className="relative bg-gradient-to-br from-amber-50 via-white to-rose-50 rounded-3xl p-8 max-w-xs w-full text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setBadge(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
              data-testid="first-badge-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            <div className="flex items-center justify-center gap-1 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-600">
                Your first badge
              </span>
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <div className="flex justify-center my-4">
              <Badge
                badge={{
                  badge_id: badge.badge_id,
                  name: badge.name,
                  description: badge.description,
                  icon: badge.icon || "award",
                  earned: true,
                }}
                size="feature"
              />
            </div>
            <h3 className="text-lg font-black text-slate-800" data-testid="first-badge-name">
              {badge.name}
            </h3>
            {badge.description && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">{badge.description}</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
